import { type ReactNode, useCallback, useState } from "react";
import { ConfirmModal } from "../components/ConfirmModal";
import { manageAccessWarning } from "../lib/grantAssembly";
import type { GrantEntry } from "../../shared/rbac/roles";

// Guards a grant commit behind a confirmation when the change would either drop
// the viewer's own manage access or leave the scope with no manager at all
// (orphaning it so only tenant admins can manage it). Shared by every grant
// editor — scopes via useScopeGrants and user groups — so the warning copy and
// trigger conditions stay identical across the app.
export function useManageAccessGuard({
  allGrants,
  doCommit,
  isManagerGrant,
  viewerRetainsManage,
  resourceLabel,
  enabled = true,
}: {
  allGrants: GrantEntry[];
  doCommit: (grants: GrantEntry[]) => void;
  isManagerGrant: (grant: GrantEntry) => boolean;
  viewerRetainsManage: (grants: GrantEntry[]) => boolean;
  resourceLabel: string;
  enabled?: boolean;
}): {
  commitGrants: (nextGrants: GrantEntry[]) => void;
  confirmModal: ReactNode;
} {
  const [pending, setPending] = useState<{
    grants: GrantEntry[];
    title: string;
    description: string;
  } | null>(null);

  const commitGrants = useCallback(
    (nextGrants: GrantEntry[]) => {
      const warning = enabled
        ? manageAccessWarning({
            current: allGrants,
            next: nextGrants,
            isManagerGrant,
            viewerRetainsManage,
          })
        : null;
      if (warning) {
        setPending({
          grants: nextGrants,
          title:
            warning === "viewer-loses"
              ? "Lose manage access?"
              : "Remove the last manager?",
          description:
            warning === "viewer-loses"
              ? `You will no longer be able to manage this ${resourceLabel} after this change.`
              : `This ${resourceLabel} will have no manager. Only tenant admins will be able to manage it.`,
        });
        return;
      }
      doCommit(nextGrants);
    },
    [
      enabled,
      allGrants,
      isManagerGrant,
      viewerRetainsManage,
      resourceLabel,
      doCommit,
    ]
  );

  const confirmModal = (
    <ConfirmModal
      open={pending !== null}
      onClose={() => setPending(null)}
      onConfirm={() => {
        if (pending) doCommit(pending.grants);
        setPending(null);
      }}
      title={pending?.title ?? ""}
      description={pending?.description ?? ""}
      confirmLabel="Continue"
      destructive
    />
  );

  return { commitGrants, confirmModal };
}
